import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react'
import { EProgramTypes } from '@ucheba/utils/types/program'
import { useDispatch, useSelector } from 'react-redux'
import { institutionsSelectors, institutionsThunks } from '@ucheba/store/institutions'
import { IWhereAreStudyingProps } from './types'
import { getProgramTypeWithChilds } from '../../bll/utils'

interface IUseWhereAreStudyingProps {
  (props: IWhereAreStudyingProps): {
    href: string
    buttonText: string
    navItems: {
      name: string
      slug: string
      value: EProgramTypes
    }[]
    activeIndex: number
    setActiveIndex: Dispatch<SetStateAction<number>>
    handleClick: (e: any, slug: string) => void
    activeProgramType: EProgramTypes
    total: number
    getMoreSpecialitiesByProf: () => void
  }
}

export const fetchInstitutionsByProfession = async (
  programType: EProgramTypes,
  tagsId: number[] | null | undefined,
  specialityIds: number[] | null | undefined,
  dispatch: Dispatch<any>
): Promise<any> => {
  const args = {
    limit: 5,
    programTypes: getProgramTypeWithChilds(programType),
  }
  if (
    programType === EProgramTypes.course ||
    (Array.isArray(programType) && programType.includes(1004))
  ) {
    await dispatch(
      institutionsThunks.fetch({
        data: {
          ...args,
          tagIds: tagsId,
        },
      })
    )
  } else {
    await dispatch(
      institutionsThunks.fetch({
        data: {
          ...args,
          specialityIds,
        },
      })
    )
  }
}

export const useWhereAreStudying: IUseWhereAreStudyingProps = (props) => {
  const { tagsId, programTypes, specialityIds } = props

  const total = useSelector(institutionsSelectors.total)

  const [activeIndex, setActiveIndex] = useState(0)
  const [activeProgramType, setActiveProgramType] = useState(
    programTypes[0].value || 1002
  )

  const dispatch = useDispatch()
  const navItems = useMemo(() => {
    return programTypes.map((item) => {
      return {
        name: item.name,
        slug: item.name,
        value: item.value,
      }
    })
  }, [programTypes])

  const fetch = useCallback(
    async (programType: EProgramTypes) => {
      await fetchInstitutionsByProfession(programType, tagsId, specialityIds, dispatch)
    },

    [dispatch, specialityIds, tagsId]
  )

  const isVuz = useMemo(
    () => activeProgramType === EProgramTypes.higher,
    [activeProgramType]
  )
  const isCollage = useMemo(
    () => activeProgramType === EProgramTypes.vocational,
    [activeProgramType]
  )
  const isCourse = useMemo(
    () => activeProgramType === EProgramTypes.course,
    [activeProgramType]
  )

  const href = useMemo(() => {
    if (isVuz) {
      if (!specialityIds) return `/for-abiturients/vuz`

      const specialityIdsStr = specialityIds
        ?.map((id, key) => {
          const paramName = encodeURIComponent(`eq[${key}]`)
          const paramValue = encodeURIComponent(`__sp:${id}`)

          return `${paramName}=${paramValue}`
        })
        .join('&')
      return `/for-abiturients/vuz?${specialityIdsStr}`
    }
    if (isCollage) {
      if (!specialityIds) return `/for-abiturients/college`

      const specialityIdsStr = specialityIds
        ?.map((id, key) => {
          const paramName = encodeURIComponent(`eq[${key}]`)
          const paramValue = encodeURIComponent(`__sp:${id}`)
          return `${paramName}=${paramValue}`
        })
        .join('&')

      return `/for-abiturients/college?${specialityIdsStr}`
    }

    if (isCourse) {
      if (!tagsId) return `/for-specialists/courses`

      const tagIdsStr = tagsId
        ?.map((id, key) => {
          const paramName = encodeURIComponent(`eq[${key}]`)
          const paramValue = encodeURIComponent(`__t:${id}`)

          return `${paramName}=${paramValue}`
        })
        .join('&')

      return `/for-specialists/courses?${tagIdsStr}`
    }

    return `/for-abiturients/vuz`
  }, [isVuz, isCollage, isCourse, specialityIds, tagsId])

  const buttonText = useMemo(() => {
    if (isVuz) {
      return `Показать все вузы`
    }

    if (isCollage) {
      return `Показать все колледжи`
    }

    if (isCourse) {
      return `Показать все курсы`
    }

    return 'Показать все'
  }, [isCollage, isCourse, isVuz])

  const handleClick = useCallback(
    (_, slug) => {
      const index = navItems.findIndex((item) => item.slug === slug)

      setActiveIndex(index)

      if (index === activeIndex) return

      const item = navItems.find((el) => el.slug === slug)

      if (item) {
        setActiveProgramType(item.value)
        fetch(item.value)
      }
    },
    [activeIndex, fetch, navItems]
  )

  const getMoreSpecialitiesByProf = useCallback(() => {}, [])

  return {
    href,
    buttonText,
    navItems,
    activeIndex,
    setActiveIndex,
    handleClick,
    activeProgramType,
    total,
    getMoreSpecialitiesByProf,
  }
}
